import React, { useState, useEffect } from 'react';
import { Package, Download } from 'lucide-react';
import { useDeliveries } from '../context/DeliveryContext';
import { exportOrdersCsv } from '../utils/csvExport';
import Header from './layout/Header';
import SearchBar from './ui/SearchBar';
import FilterTabs from './ui/FilterTabs';
import StatsCards from './ui/StatsCards';
import DeliveryCard from './DeliveryCard';
import AddNoteModal from './modals/AddNoteModal';
import CompleteConfirmModal from './modals/CompleteConfirmModal';
import LoadingSpinner from './ui/LoadingSpinner';

export default function Dashboard() {
  const {
    deliveries,
    loading,
    error,
    statusCounts,
    loadDeliveries,
    markAsActioned,
    addNote,
  } = useDeliveries();

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [noteModal, setNoteModal] = useState({ isOpen: false, delivery: null });
  const [completeModal, setCompleteModal] = useState({ isOpen: false, delivery: null });
  const [isProcessing, setIsProcessing] = useState(false);

  // Load deliveries on mount and when filters change
  useEffect(() => {
    loadDeliveries({ orderType: typeFilter, status: statusFilter, search: searchTerm });
  }, [typeFilter, statusFilter, loadDeliveries]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadDeliveries({ orderType: typeFilter, status: statusFilter, search: searchTerm });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, typeFilter, statusFilter, loadDeliveries]);

  const handleAddNote = async (noteText) => {
    if (noteModal.delivery) {
      try {
        await addNote(noteModal.delivery.id, noteText);
      } catch (err) {
        console.error('Failed to add note:', err);
      }
    }
  };

  const handleComplete = async () => {
    if (completeModal.delivery) {
      setIsProcessing(true);
      try {
        await markAsActioned(completeModal.delivery);
        setCompleteModal({ isOpen: false, delivery: null });
      } catch (err) {
        console.error('Failed to mark as actioned:', err);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-locate2-bg-light">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Page Title */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-locate2-dark-blue mb-1">Imports</h1>
            <p className="text-locate2-light-gray">Manage and track all import orders</p>
          </div>
          <button
            onClick={() => exportOrdersCsv(deliveries)}
            disabled={loading || deliveries.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-locate2-primary text-white rounded-lg hover:bg-locate2-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-locate2-border p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by customer name or order reference..."
            />
            <FilterTabs
              activeTypeFilter={typeFilter}
              activeStatusFilter={statusFilter}
              onTypeFilterChange={setTypeFilter}
              onStatusFilterChange={setStatusFilter}
              counts={statusCounts}
            />
          </div>
        </div>

        {/* Statistics Cards */}
        <StatsCards counts={statusCounts} />

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-status-error-bg border border-status-error rounded-lg text-status-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Deliveries List */}
        {!loading && (
          <div>
            {deliveries.length === 0 ? (
              <div className="bg-white rounded-lg border border-locate2-border p-8 text-center">
                <Package size={48} className="mx-auto text-locate2-disabled mb-4" />
                <h3 className="text-locate2-dark-blue font-semibold mb-2">No imports found</h3>
                <p className="text-locate2-light-gray">
                  Try adjusting your search or filter criteria
                </p>
              </div>
            ) : (
              deliveries.map((delivery) => (
                <DeliveryCard
                  key={delivery.id}
                  delivery={delivery}
                  isExpanded={expandedId === delivery.id}
                  onToggle={() =>
                    setExpandedId(expandedId === delivery.id ? null : delivery.id)
                  }
                  onAddNote={(d) => setNoteModal({ isOpen: true, delivery: d })}
                  onComplete={(d) => setCompleteModal({ isOpen: true, delivery: d })}
                />
              ))
            )}
          </div>
        )}
      </main>

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={noteModal.isOpen}
        onClose={() => setNoteModal({ isOpen: false, delivery: null })}
        onSubmit={handleAddNote}
        deliveryName={noteModal.delivery?.name || ''}
      />

      {/* Complete Confirmation Modal */}
      <CompleteConfirmModal
        isOpen={completeModal.isOpen}
        onClose={() => setCompleteModal({ isOpen: false, delivery: null })}
        onConfirm={handleComplete}
        deliveryName={completeModal.delivery?.name || ''}
        isProcessing={isProcessing}
      />
    </div>
  );
}
